// The DSE listed universe as the desk sees it.
//
// PROVENANCE: the 22 tickers are exactly the symbol files dse-scraper captured on its last
// session (2026-07-31) from DSE's own onlinetrading.dse.co.tz portal. The tier and ETF
// classification is transcribed verbatim from
// /home/shabba/dse-scraper/shabba_dse/market_research.py:502-505 (TIER1_STOCKS /
// TIER2_STOCKS / TIER3_STOCKS / ETF_STOCKS).
//
// WHAT IS DELIBERATELY ABSENT:
//   - Company / fund long names. They exist nowhere in the org today. Hand-inventing 22 of
//     them would put fabricated text on a trading surface, so the column is omitted rather
//     than guessed.
//   - sec_ids for the 16 counters we do not quote. Those come from DSE's security master
//     over a FIX path that has never completed a logon. A synthetic 4-letter code would
//     collide with the real namespace and poison every downstream join, so it stays null
//     and the table is keyed on ticker.

import { INSTRUMENTS } from './instruments';

export type DseTier = 'TIER1' | 'TIER2' | 'TIER3' | 'ETF';

export const TIER_LABEL: Record<DseTier, string> = {
  TIER1: 'Blue chip',
  TIER2: 'Mid cap',
  TIER3: 'Small / illiquid',
  ETF: 'ETF',
};

export interface DseCounter {
  ticker: string;
  tier: DseTier;
  /** Our DSE security id, present only for the counters the engine is subscribed to. */
  secId: string | null;
  /** True when the market-making engine quotes this counter. */
  quoted: boolean;
}

const TIER1 = ['NMB', 'CRDB', 'TBL', 'VODA', 'TCC'];
const TIER2 = ['DSE', 'TPCC', 'SWIS', 'NICO', 'TCCL', 'TOL', 'MBP'];
const TIER3 = ['DCB', 'MCB', 'MKCB', 'MUCOBA', 'PAL', 'TTP', 'AFRIPRISE'];
const ETFS = ['IEACLC-ETF', 'VERTEX-ETF'];

/** Every symbol dse-scraper captured on 2026-07-31, its last session. */
const CAPTURED = [
  'AFRIPRISE', 'CRDB', 'DCB', 'DSE', 'IEACLC-ETF', 'KCB', 'MBP', 'MCB', 'MKCB', 'MUCOBA',
  'NICO', 'NMB', 'PAL', 'SWIS', 'TBL', 'TCC', 'TCCL', 'TOL', 'TPCC', 'TTP', 'VERTEX-ETF', 'VODA',
];

function tierOf(ticker: string): DseTier {
  if (ETFS.includes(ticker)) return 'ETF';
  if (TIER1.includes(ticker)) return 'TIER1';
  if (TIER2.includes(ticker)) return 'TIER2';
  return 'TIER3';
}

const SEC_ID_BY_TICKER: Record<string, string> = INSTRUMENTS.reduce<Record<string, string>>((m, i) => {
  m[i.label.toUpperCase()] = i.secId;
  return m;
}, {});

/**
 * The universe, ETFs first then by tier then alphabetically — so the counters we cannot
 * yet price sort to a predictable place rather than interleaving with the ones we quote.
 */
export const DSE_UNIVERSE: DseCounter[] = CAPTURED
  .map(ticker => ({
    ticker,
    tier: tierOf(ticker),
    secId: SEC_ID_BY_TICKER[ticker] ?? null,
    quoted: SEC_ID_BY_TICKER[ticker] != null,
  }))
  .sort((a, b) => {
    const order: Record<DseTier, number> = { ETF: 0, TIER1: 1, TIER2: 2, TIER3: 3 };
    if (order[a.tier] !== order[b.tier]) return order[a.tier] - order[b.tier];
    return a.ticker.localeCompare(b.ticker);
  });

export const UNIVERSE_SIZE = DSE_UNIVERSE.length;
export const QUOTED_COUNT = DSE_UNIVERSE.filter(c => c.quoted).length;

/**
 * The last session dse-scraper captured. The scraper is NOT currently running — there is
 * no cron entry beyond an `@reboot` line, and `pgrep -af run_complete` finds nothing — so
 * this date does not advance on its own. Any surface citing it must show it.
 */
export const LAST_CAPTURE_DATE = '2026-07-31';
